/**
 * Extra tab helpers — AFAS salary-scale cascade like bluebox Question12→13→14→15.
 */
const CASCADE_PARENT = {
    soortLoonschaal: 'soortSalaris',
    loonschaal: 'soortLoonschaal',
    trede: 'loonschaal',
};

export const mergeExtraColumnOptions = (columns = [], optionsByField = {}) =>
    columns.map((column) => ({
        ...column,
        fields: column.fields.map((field) => {
            if (field.type !== 'select') {
                return field;
            }

            const options = optionsByField[field.id];
            if (!Array.isArray(options) || options.length === 0) {
                return field;
            }

            return {
                ...field,
                options: options.map((option) => ({
                    value: String(option.value ?? ''),
                    label: String(option.label ?? option.value ?? ''),
                    ...(option.selector != null && option.selector !== ''
                        ? { selector: String(option.selector) }
                        : {}),
                })),
            };
        }),
    }));

export const resolveSelectValue = (value, options = []) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const raw = String(value);
    const byValue = options.find((option) => String(option.value) === raw);
    if (byValue) {
        return byValue.value;
    }

    const byLabel = options.find((option) => String(option.label) === raw);
    return byLabel ? byLabel.value : raw;
};

export const filterExtraFieldOptions = (field, values = {}) => {
    const parentId = CASCADE_PARENT[field.id];
    if (!parentId) {
        return field.options || [];
    }

    const parentValue = String(values[parentId] ?? '');
    if (!parentValue) {
        return [];
    }

    return (field.options || []).filter(
        (option) => String(option.selector ?? '') === parentValue,
    );
};

const optionsForField = (optionsByField, fieldId, parentValue) => {
    const options = optionsByField[fieldId] || [];
    if (!parentValue) {
        return options;
    }

    return options.filter((option) => String(option.selector ?? '') === String(parentValue));
};

export const applyExtraFieldChange = (values, fieldId, value, optionsByField = {}) => {
    let next = {
        ...values,
        [fieldId]: value,
    };

    if (fieldId === 'soortSalaris') {
        next.soortLoonschaal = '';
        next.loonschaal = '';
        next.trede = '';
        next = autoSelectIfSingle(next, 'soortLoonschaal', optionsByField, value);
    } else if (fieldId === 'soortLoonschaal') {
        next.loonschaal = '';
        next.trede = '';
        next = autoSelectIfSingle(next, 'loonschaal', optionsByField, value);
    } else if (fieldId === 'loonschaal') {
        next.trede = '';
        next = autoSelectIfSingle(next, 'trede', optionsByField, value);
    }

    return next;
};

const autoSelectIfSingle = (values, fieldId, optionsByField, parentValue) => {
    const options = optionsForField(optionsByField, fieldId, parentValue);
    if (options.length !== 1) {
        return values;
    }

    return applyExtraFieldChange(values, fieldId, String(options[0].value), optionsByField);
};

export const normalizeExtraValues = (values = {}, optionsByField = {}) => {
    const next = { ...values };
    if (next.periodenummerPeriodiek == null && next.formatieruimtePeriode != null) {
        next.periodenummerPeriodiek = next.formatieruimtePeriode;
    }

    Object.entries(optionsByField).forEach(([fieldId, options]) => {
        if (!Array.isArray(options) || next[fieldId] == null || next[fieldId] === '') {
            return;
        }

        next[fieldId] = resolveSelectValue(next[fieldId], options);
    });

    return next;
};

/**
 * Load-time Extra defaults: remap labels to codes and auto-fill single cascade children (bluebox SelectedItems).
 */
export const hydrateExtraValues = (values = {}, optionsByField = {}) => {
    const next = normalizeExtraValues(values, optionsByField);

    if (!next.soortSalaris) {
        next.soortSalaris = 'S';
    }

    const fillEmptyOrResolve = (fieldId, parentValue) => {
        const scoped = optionsForField(optionsByField, fieldId, parentValue);
        const lookup = scoped.length > 0 ? scoped : (optionsByField[fieldId] || []);

        if (next[fieldId] == null || next[fieldId] === '') {
            if (scoped.length === 1) {
                next[fieldId] = String(scoped[0].value);
            }
            return;
        }

        next[fieldId] = resolveSelectValue(next[fieldId], lookup);
    };

    fillEmptyOrResolve('soortLoonschaal', next.soortSalaris);

    if (next.loonschaal) {
        next.loonschaal = resolveSelectValue(next.loonschaal, optionsByField.loonschaal || []);
        const matches = (optionsByField.loonschaal || []).filter(
            (option) => String(option.value) === String(next.loonschaal),
        );
        const selectors = [...new Set(
            matches.map((option) => String(option.selector || '')).filter(Boolean),
        )];
        if (selectors.length === 1) {
            next.soortLoonschaal = selectors[0];
        } else if (selectors.length > 1 && !selectors.includes(String(next.soortLoonschaal || ''))) {
            next.soortLoonschaal = '';
        }
    }

    fillEmptyOrResolve('loonschaal', next.soortLoonschaal);
    fillEmptyOrResolve('trede', next.loonschaal);

    return next;
};

export const withCurrentOption = (options = [], value) => {
    if (value === null || value === undefined || value === '') {
        return options;
    }

    const raw = String(value);
    if (options.some((option) => String(option.value) === raw)) {
        return options;
    }

    return [...options, { value: raw, label: raw }];
};
